// crewbit-admin/src/components/money/settings/InviteAccountantCard.tsx
//
// §Money.StubActionSweepCompletion Phase 4 (Decision #576). REUSES the
// existing team_invites substrate via `create_team_invite` RPC with
// role='accountant'. Migration 293 extended the RPC's role validation to
// accept 'accountant'; the table itself had no CHECK gate.
//
// Drift caught at Step 0 vs spec §4.4: the existing team_invites flow does
// NOT send magic-link emails — it generates a 6-char invite code that the
// operator shares manually. This card mirrors that pattern: enter name,
// generate share URL, copy to clipboard. Email-sending infra is a separate
// substrate session.
//
// Mirrors crewbit-mobile/components/money/settings/InviteAccountantCard.tsx.

import { useState } from 'react';
import { Sparkles, Copy, CheckCircle2 } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import { useTeamInvites } from '../../../hooks/useTeamInvites';
import { useActiveBusinessStore } from '../../../hooks/useActiveBusiness';

const INVITE_ERROR_COPY: Record<string, string> = {
  not_authorized: "You don't have permission to invite accountants.",
  invalid_role: "Server rejected the accountant role — refresh and try again.",
  name_required: "Enter a name or email to invite.",
  code_generation_failed: "Couldn't generate an invite code. Try again.",
  network_error: "Network failed. Check your connection.",
  not_authenticated: "Sign in to continue.",
};

export default function InviteAccountantCard() {
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const { createInvite } = useTeamInvites(businessId ?? undefined);

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError(INVITE_ERROR_COPY.name_required);
      return;
    }
    setSubmitting(true);
    const result = await createInvite(name.trim(), 'accountant');
    setSubmitting(false);
    if (result.success && result.share_url) {
      setShareUrl(result.share_url);
      setName('');
    } else {
      setError(INVITE_ERROR_COPY[result.error_code ?? 'network_error'] ?? 'Failed to create invite.');
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy link. Select and copy manually.');
    }
  };

  return (
    <div style={{
      background: palette.white,
      borderRadius: radii.lg,
      border: `1px solid ${palette.g150}`,
      padding: spacing.md,
      display: 'flex', flexDirection: 'column', gap: spacing.sm,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 18,
          background: palette.accentWash,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Sparkles size={18} color={palette.accent} strokeWidth={2} />
        </div>
        <span style={{
          flex: 1, minWidth: 0,
          fontSize: typography.size.heading, fontWeight: 700, color: palette.g800,
        }}>
          Invite your accountant
        </span>
      </div>
      <div style={{ fontSize: typography.size.body, color: palette.g600, lineHeight: 1.5 }}>
        Generate an invite link your accountant can claim to access your books.
        They join as a scoped role (no extra team seat used). Audit-trail logged.
      </div>

      {!shareUrl && (
        <>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 200))}
            placeholder="Name or email"
            disabled={submitting}
            style={{
              width: '100%', padding: spacing.sm, borderRadius: 8,
              border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
              fontSize: typography.size.caption, color: palette.g800,
              boxSizing: 'border-box', background: palette.white,
            }}
          />
          {error && (
            <div style={{ fontSize: typography.size.caption, color: palette.red }}>{error}</div>
          )}
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || !name.trim()}
            style={{
              alignSelf: 'flex-start',
              padding: '9px 22px', borderRadius: 20, border: 'none',
              background: (submitting || !name.trim()) ? palette.g300 : palette.accent,
              color: palette.white, fontSize: 13.5, fontWeight: 600,
              cursor: (submitting || !name.trim()) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {submitting ? 'Creating…' : 'Create invite'}
          </button>
        </>
      )}

      {shareUrl && (
        <div style={{
          padding: spacing.sm,
          background: palette.greenWash,
          border: `1px solid ${palette.green}`,
          borderRadius: 8,
          display: 'flex', flexDirection: 'column', gap: spacing.xs,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, fontSize: typography.size.caption, color: palette.g800 }}>
            <CheckCircle2 size={14} color={palette.green} />
            Invite created. Share this link with your accountant:
          </div>
          <div style={{
            padding: spacing.xs,
            background: palette.white,
            border: `1px solid ${palette.g200}`,
            borderRadius: 6,
            fontFamily: 'monospace',
            fontSize: typography.size.caption,
            wordBreak: 'break-all',
            color: palette.g800,
          }}>
            {shareUrl}
          </div>
          <div style={{ display: 'flex', gap: spacing.xs }}>
            <button
              type="button"
              onClick={() => void handleCopy()}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 999,
                border: `1px solid ${palette.g200}`, background: palette.white,
                fontSize: 12, fontWeight: 500, color: palette.g700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <Copy size={12} />
              {copied ? 'Copied!' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={() => { setShareUrl(null); setCopied(false); }}
              style={{
                padding: '6px 12px', borderRadius: 999,
                border: `1px solid ${palette.g200}`, background: palette.white,
                fontSize: 12, fontWeight: 500, color: palette.g700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Invite another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
